import { Pin, PinOff } from '@/components/ui/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { MessageDTO, MessageTargetRef } from '@clanfire/shared';
import { messageApi } from '@/api/endpoints';
import { Avatar } from '@/components/ui/Avatar';
import { IconButton } from '@/components/ui/Button';
import { EmptyState, ErrorState } from '@/components/ui/Feedback';
import { Popover } from '@/components/ui/Popover';
import { Spinner } from '@/components/ui/Spinner';
import { setPinned } from '@/lib/messaging';
import { formatMessageTime } from '@/lib/time';
import { targetKey, useMessages } from '@/stores/messages';
import { useUi } from '@/stores/ui';
import { useUser } from '@/stores/users';
import { MessageContent } from './MessageContent';

/** Top-bar button with a popover listing the channel's (or DM's) pinned messages. */
export function PinsButton({ target, canPin }: { target: MessageTargetRef; canPin: boolean }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<{ status: 'loading' | 'ready' | 'error'; messages: MessageDTO[] }>({ status: 'loading', messages: [] });
  const anchor = useRef<HTMLButtonElement>(null);
  const key = targetKey(target);
  const requestJump = useUi((s) => s.requestJump);
  // Any pin/unpin in view (ours or someone else's) arrives as a message update; refetch when open.
  const pinSignature = useMessages((s) =>
    (s.byTarget[key]?.messages ?? [])
      .filter((m) => m.pinnedAt)
      .map((m) => m.id)
      .join(','),
  );

  const load = useCallback(async () => {
    setState((s) => ({ ...s, status: s.messages.length ? 'ready' : 'loading' }));
    try {
      const { messages } = await messageApi.pins(target);
      setState({ status: 'ready', messages });
    } catch {
      setState((s) => ({ ...s, status: 'error' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    if (open) void load();
  }, [open, load, pinSignature]);

  return (
    <>
      <IconButton ref={anchor} label="Pinned messages" active={open} onClick={() => setOpen((v) => !v)}>
        <Pin size={20} />
      </IconButton>
      <Popover open={open} onClose={() => setOpen(false)} anchor={anchor} placement="bottom-end" label="Pinned messages" className="flex w-[min(420px,calc(100vw-16px))] flex-col">
        <div className="border-b border-line px-4 py-3">
          <h2 className="font-bold">Pinned messages</h2>
        </div>
        <div className="max-h-[min(520px,70dvh)] overflow-y-auto p-1.5">
          {state.status === 'loading' ? (
            <div className="flex justify-center py-10 text-fg-muted">
              <Spinner />
            </div>
          ) : state.status === 'error' ? (
            <ErrorState message="Could not load pinned messages." onRetry={() => void load()} />
          ) : state.messages.length === 0 ? (
            <EmptyState art="pins" title="No pins yet" body={canPin ? 'Pin important messages from their ⋯ menu so everyone can find them.' : 'Important messages pinned here will show up in this list.'} />
          ) : (
            <ul className="flex flex-col gap-1.5">
              {state.messages.map((m) => (
                <PinnedItem
                  key={m.id}
                  message={m}
                  canPin={canPin}
                  onJump={() => {
                    setOpen(false);
                    requestJump(key, m.id);
                  }}
                  onUnpin={async () => {
                    if (await setPinned(m, false)) setState((s) => ({ ...s, messages: s.messages.filter((x) => x.id !== m.id) }));
                  }}
                />
              ))}
            </ul>
          )}
        </div>
      </Popover>
    </>
  );
}

function PinnedItem({ message, canPin, onJump, onUnpin }: { message: MessageDTO; canPin: boolean; onJump: () => void; onUnpin: () => void }) {
  const author = useUser(message.author?.id, message.author);
  return (
    <li className="group relative rounded-xl border border-line bg-surface p-3 transition-colors hover:border-line-strong">
      <div className="flex gap-2.5">
        <Avatar user={author} size={28} />
        <div className="min-w-0 flex-1">
          <p className="flex items-baseline gap-2">
            <span className="truncate text-sm font-semibold">{author?.displayName ?? 'Deleted User'}</span>
            <span className="shrink-0 text-[11px] text-fg-muted">{formatMessageTime(message.createdAt)}</span>
          </p>
          <MessageContent content={message.content.length > 400 ? `${message.content.slice(0, 400)}…` : message.content} serverId={message.serverId} interactive={false} className="text-sm text-fg-soft" />
          {message.attachments.length > 0 && <p className="mt-1 text-xs text-fg-muted">📎 {message.attachments.length === 1 ? '1 attachment' : `${message.attachments.length} attachments`}</p>}
        </div>
      </div>
      <div className="absolute top-2 right-2 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
        <button type="button" onClick={onJump} className="rounded-md bg-elevated px-2 py-1 text-xs font-semibold text-fg shadow-sm hover:bg-active">
          Jump
        </button>
        {canPin && (
          <button type="button" onClick={onUnpin} aria-label="Unpin message" title="Unpin" className="rounded-md bg-elevated p-1 text-fg-muted shadow-sm hover:bg-active hover:text-danger">
            <PinOff size={14} />
          </button>
        )}
      </div>
    </li>
  );
}
